/**
 * affiliate-tabs.tsx — Các tab của trang Tiếp thị liên kết (đối tác): khách đã giới thiệu (tên che), hoa hồng, rút tiền, hồ sơ.
 */
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Info, Loader2 } from 'lucide-react'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { cn } from '@/lib/utils'
import { currentLocale } from '@/lib/i18n'
import {
  commissionStatusLabel, EMPTY_REGISTER_FORM, estimateNet, formatSignedVnd, referralSourceLabel, suspendedBanner, taxRuleLabel, validateRegisterForm,
  withdrawalStatusLabel, WITHDRAWAL_STATUS_TONE, type CommissionItem, type PartnerView, type PublicProgram, type RegisterForm, type WithdrawalItem,
} from '@/lib/affiliate'
import {
  useAffiliateCommissions, useAffiliateReferrals, useAffiliateWithdrawals, useCancelWithdrawal, useCreateWithdrawal, useUpdatePayoutInfo,
  useUpdateProfile, type ReferralItem,
} from '@/hooks/use-affiliate'
import { PayoutFields, ProfileFields } from './partner-form'
import { TaxCertificateCard } from './tax-certificate'
import { api } from '@/lib/api-client'

const fmtDate = (iso: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY') : '—')

// ── Khách đã giới thiệu ─────────────────────────────────────────────────

export function ReferralsTab() {
  const { t } = useTranslation('growth')
  const { data, isLoading } = useAffiliateReferrals(true)
  const columns: Column<ReferralItem>[] = [
    { key: 'customer', header: t('affiliateTabs.referrals.customer'), cell: (r) => <span className="font-medium">{r.customer}</span> },
    { key: 'source', header: t('affiliateTabs.referrals.source'), cell: (r) => referralSourceLabel(r.source) },
    { key: 'at', header: t('affiliateTabs.referrals.at'), cell: (r) => fmtDate(r.attributedAt) },
    { key: 'paying', header: t('affiliateTabs.referrals.paying'), cell: (r) => r.paying ? <Badge variant="success">{t('affiliateTabs.referrals.paid')}</Badge> : <Badge variant="secondary">{t('affiliateTabs.referrals.unpaid')}</Badge> },
    { key: 'until', header: t('affiliateTabs.referrals.until'), cell: (r) => r.commissionUntil ? fmtDate(r.commissionUntil) : t('affiliateTabs.referrals.unlimited') },
    { key: 'orders', header: t('affiliateTabs.referrals.orders'), align: 'right', cell: (r) => r.orders },
    { key: 'commission', header: t('affiliateTabs.referrals.commission'), align: 'right', cell: (r) => formatVnd(r.commission) },
  ]
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t('affiliateTabs.referrals.privacyHint')}</p>
      <DataTable columns={columns} rows={data ?? []} loading={isLoading} rowKey={(r) => r.id} emptyTitle={t('affiliateTabs.referrals.empty')} />
    </div>
  )
}

// ── Hoa hồng ────────────────────────────────────────────────────────────

export function CommissionsTab() {
  const { t } = useTranslation('growth')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading } = useAffiliateCommissions(true, status, page)
  const columns: Column<CommissionItem>[] = [
    { key: 'date', header: t('affiliateTabs.commissions.date'), cell: (c) => fmtDate(c.orderPaidAt ?? c.createdAt) },
    {
      key: 'what', header: t('affiliateTabs.commissions.what'),
      cell: (c) => c.kind === 'adjustment'
        ? <span>{t('affiliateTabs.commissions.adjustment', { reason: c.reason })}</span>
        : <span>{c.customer} <span className="text-xs text-muted-foreground">{t('affiliateTabs.commissions.orderSuffix', { id: c.orderId })}</span></span>,
    },
    {
      key: 'base', header: t('affiliateTabs.commissions.base'), align: 'right',
      cell: (c) => c.kind === 'commission' ? formatVnd(c.baseAmount) : '—',
    },
    {
      key: 'rate', header: t('affiliateTabs.commissions.rate'), cell: (c) => c.rateType === 'percent' ? `${c.rateValue}%` : c.rateType === 'fixed' ? t('affiliateTabs.commissions.fixed') : '—',
    },
    { key: 'amount', header: t('affiliateTabs.commissions.amount'), align: 'right', cell: (c) => <span className={cn('font-medium', c.amount < 0 && 'text-destructive')}>{formatSignedVnd(c.amount)}</span> },
    {
      key: 'status', header: t('affiliateTabs.commissions.status'),
      cell: (c) => (
        <div>
          <Badge variant={c.status === 'available' ? 'success' : c.status === 'void' ? 'destructive' : 'warning'}>{commissionStatusLabel(c.status)}</Badge>
          {c.status === 'holding' && c.holdUntil && <p className="mt-0.5 text-xs text-muted-foreground">{t('affiliateTabs.commissions.availableFrom', { date: fmtDate(c.holdUntil) })}</p>}
          {c.status === 'void' && c.voidReason && <p className="mt-0.5 text-xs text-muted-foreground">{c.voidReason}</p>}
        </div>
      ),
    },
  ]
  const filters: [string, string][] = [['', t('affiliateTabs.commissions.filterAll')], ['holding', t('affiliateTabs.commissions.filterHolding')], ['available', t('affiliateTabs.commissions.filterAvailable')], ['void', t('affiliateTabs.commissions.filterVoid')]]
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1">
        {filters.map(([v, l]) => (
          <Button key={v} size="sm" variant={status === v ? 'default' : 'outline'} onClick={() => { setStatus(v); setPage(1) }}>{l}</Button>
        ))}
      </div>
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(c) => c.id} emptyTitle={t('affiliateTabs.commissions.empty')} />
      {data && data.total > data.limit && <Pagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />}
    </div>
  )
}

// ── Rút tiền ────────────────────────────────────────────────────────────

export function WithdrawalsTab({ partner, program }: { partner: PartnerView; program: PublicProgram }) {
  const { t } = useTranslation('growth')
  const { data, isLoading } = useAffiliateWithdrawals(true)
  const create = useCreateWithdrawal()
  const cancel = useCancelWithdrawal()
  const [amountText, setAmountText] = useState('')
  const amount = Number(amountText.replace(/\D/g, '')) || 0
  const available = Math.max(0, data?.balance.available ?? 0)
  const hasOpen = (data?.items ?? []).some((w) => w.status === 'pending' || w.status === 'approved')
  const est = useMemo(() => estimateNet(amount, partner, program.tax), [amount, partner, program.tax])
  const min = data?.minWithdrawal ?? program.minWithdrawal
  const suspended = partner.status !== 'active'
  const blocker = suspended ? suspendedBanner()
    : hasOpen ? t('affiliateTabs.withdrawals.hasOpen')
      : amount <= 0 ? null
        : amount < min ? t('affiliateTabs.withdrawals.belowMin', { amount: formatVnd(min) })
          : amount > available ? t('affiliateTabs.withdrawals.aboveAvailable') : null

  const submit = () => {
    if (!window.confirm(t('affiliateTabs.withdrawals.confirmCreate', { amount: formatVnd(amount), net: formatVnd(est.net) }))) return
    create.mutate(amount, {
      onSuccess: () => { toast.success(t('affiliateTabs.withdrawals.created')); setAmountText('') },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const columns: Column<WithdrawalItem>[] = [
    { key: 'code', header: t('affiliateTabs.withdrawals.code'), cell: (w) => <span className="font-mono text-xs">{w.code}</span> },
    { key: 'at', header: t('affiliateTabs.withdrawals.requestedAt'), cell: (w) => fmtDate(w.requestedAt) },
    { key: 'amount', header: t('affiliateTabs.withdrawals.amount'), align: 'right', cell: (w) => formatVnd(w.amount) },
    { key: 'tax', header: t('affiliateTabs.withdrawals.tax'), align: 'right', cell: (w) => formatVnd(w.taxWithheld) },
    { key: 'net', header: t('affiliateTabs.withdrawals.net'), align: 'right', cell: (w) => <span className="font-medium">{formatVnd(w.netAmount)}</span> },
    {
      key: 'status', header: t('affiliateTabs.withdrawals.status'),
      cell: (w) => (
        <div>
          <Badge variant={WITHDRAWAL_STATUS_TONE[w.status]}>{withdrawalStatusLabel(w.status)}</Badge>
          {w.status === 'paid' && <p className="mt-0.5 text-xs text-muted-foreground">{t('affiliateTabs.withdrawals.paidLine', { date: fmtDate(w.paidAt), ref: w.transferRef })}</p>}
          {w.status === 'rejected' && w.rejectReason && <p className="mt-0.5 text-xs text-muted-foreground">{w.rejectReason}</p>}
        </div>
      ),
    },
    {
      key: 'act', header: '', align: 'right',
      cell: (w) => w.status === 'pending' && !suspended && (
        <Button size="sm" variant="ghost" disabled={cancel.isPending} onClick={() => {
          if (window.confirm(t('affiliateTabs.withdrawals.confirmCancel', { code: w.code }))) cancel.mutate(w.id, { onError: (e) => toast.error(apiError(e)) })
        }}>{t('affiliateTabs.withdrawals.cancel')}</Button>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <Card className="space-y-4 p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">{t('affiliateTabs.withdrawals.available')}</p>
            <p className="text-2xl font-bold tabular-nums">{formatSignedVnd(data?.balance.available ?? 0)}</p>
            <p className="text-xs text-muted-foreground">
              {t('affiliateTabs.withdrawals.balanceLine', {
                holding: formatVnd(data?.balance.holding ?? 0),
                pending: formatVnd(data?.balance.pendingWithdrawal ?? 0),
                paid: formatVnd(data?.balance.paid ?? 0),
              })}
            </p>
          </div>
          <p className="text-xs text-muted-foreground">{t('affiliateTabs.withdrawals.receiveInto', { bank: partner.bankName, account: partner.bankAccountMasked, holder: partner.bankAccountHolder })}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="max-w-[220px]"
            inputMode="numeric"
            placeholder={t('affiliateTabs.withdrawals.minPlaceholder', { min: min.toLocaleString(currentLocale()) })}
            value={amountText ? Number(amountText.replace(/\D/g, '') || 0).toLocaleString(currentLocale()) : ''}
            onChange={(e) => setAmountText(e.target.value)}
          />
          <Button variant="outline" size="sm" onClick={() => setAmountText(String(available))} disabled={available <= 0}>{t('affiliateTabs.withdrawals.withdrawAll')}</Button>
          <Button onClick={submit} disabled={!!blocker || amount <= 0 || create.isPending}>
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {t('affiliateTabs.withdrawals.create')}
          </Button>
        </div>
        {amount > 0 && (
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <p>{taxRuleLabel(est.rule)}{est.rule === 'withhold' ? ` ${est.ratePercent}%: −${formatVnd(est.taxWithheld)}` : ''}</p>
            <p className="font-medium">{t('affiliateTabs.withdrawals.estimatedNet', { amount: formatVnd(est.net) })}</p>
            <p className="text-xs text-muted-foreground">{t('affiliateTabs.withdrawals.taxRecalc')}</p>
          </div>
        )}
        {blocker && <p className="text-sm text-destructive">{blocker}</p>}
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t('affiliateTabs.withdrawals.processNote')}
          {program.tax.mode === 'withhold_individual' && ` ${t('affiliateTabs.withdrawals.withholdNote', { percent: program.tax.ratePercent, threshold: formatVnd(program.tax.thresholdPerPayment) })}`}
          {partner.partnerType === 'company' && program.tax.companyRequiresInvoice && ` ${t('affiliateTabs.withdrawals.invoiceNote')}`}
        </p>
      </Card>
      <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(w) => w.id} emptyTitle={t('affiliateTabs.withdrawals.empty')} />
      <TaxCertificateCard
        load={async (params) => (await api.get<string>('/affiliate/tax-certificate', { params, responseType: 'text' })).data}
        errorText={apiError}
      />
    </div>
  )
}

// ── Hồ sơ & nhận tiền ───────────────────────────────────────────────────

function formFromPartner(p: PartnerView): RegisterForm {
  return {
    ...EMPTY_REGISTER_FORM,
    partnerType: p.partnerType, fullName: p.fullName, phone: p.phone, email: p.email, address: p.address,
    companyName: p.companyName ?? '', taxCode: p.taxCode ?? '', bankName: p.bankName, bankAccountHolder: p.bankAccountHolder,
    bankBranch: p.bankBranch ?? '', pitCommitment: p.pitCommitment, code: '', acceptTerms: true,
  }
}

export function ProfileTab({ partner, program }: { partner: PartnerView; program: PublicProgram }) {
  const { t } = useTranslation('growth')
  const updateProfile = useUpdateProfile()
  const updatePayout = useUpdatePayoutInfo()
  const [f, setF] = useState<RegisterForm>(() => formFromPartner(partner))
  const [errors, setErrors] = useState<Partial<Record<keyof RegisterForm, string>>>({})
  const [editPayout, setEditPayout] = useState(false)
  useEffect(() => setF(formFromPartner(partner)), [partner])
  const set = <K extends keyof RegisterForm>(k: K, v: RegisterForm[K]) => setF((s) => ({ ...s, [k]: v }))

  const saveProfile = () => {
    const errs = validateRegisterForm(f, { requirePayout: false, requireTerms: false })
    setErrors(errs)
    if (Object.keys(errs).length) return
    updateProfile.mutate({ ...f }, { onSuccess: () => toast.success(t('affiliateTabs.profile.savedProfile')), onError: (e) => toast.error(apiError(e)) })
  }
  const savePayout = () => {
    const errs = validateRegisterForm(f, { requireTerms: false })
    setErrors(errs)
    if (Object.keys(errs).length) return
    updatePayout.mutate({ ...f }, {
      onSuccess: () => { toast.success(t('affiliateTabs.profile.savedPayout')); setEditPayout(false) },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-5">
      <Card className="space-y-4 p-5">
        <div className="flex items-center justify-between">
          <p className="font-medium">{t('affiliateTabs.profile.title', { type: partner.partnerType === 'company' ? t('affiliateTabs.profile.company') : t('affiliateTabs.profile.individual') })}</p>
          <Button size="sm" onClick={saveProfile} disabled={updateProfile.isPending || partner.status !== 'active'}>
            {updateProfile.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {t('affiliateTabs.profile.save')}
          </Button>
        </div>
        <ProfileFields f={f} set={set} errors={errors} />
      </Card>
      <Card className="space-y-4 p-5">
        <div className="flex items-center justify-between">
          <p className="font-medium">{t('affiliateTabs.profile.payoutTitle')}</p>
          {!editPayout && partner.status === 'active' && <Button size="sm" variant="outline" onClick={() => setEditPayout(true)}>{t('affiliateTabs.profile.change')}</Button>}
        </div>
        {!editPayout ? (
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            {partner.partnerType === 'individual' && <><dt className="text-muted-foreground">{t('affiliateTabs.profile.idNumber')}</dt><dd>{partner.idNumberMasked}</dd></>}
            <dt className="text-muted-foreground">{t('affiliateTabs.profile.bank')}</dt><dd>{partner.bankName}{partner.bankBranch ? ` — ${partner.bankBranch}` : ''}</dd>
            <dt className="text-muted-foreground">{t('affiliateTabs.profile.bankAccount')}</dt><dd>{partner.bankAccountMasked}</dd>
            <dt className="text-muted-foreground">{t('affiliateTabs.profile.bankHolder')}</dt><dd>{partner.bankAccountHolder}</dd>
            {partner.partnerType === 'individual' && <><dt className="text-muted-foreground">{t('affiliateTabs.profile.commitment08')}</dt><dd>{partner.pitCommitment ? t('affiliateTabs.profile.submitted') : t('affiliateTabs.profile.notYet')}</dd></>}
          </dl>
        ) : (
          <>
            <div className="rounded-md border border-warning/40 bg-warning/5 p-3 text-xs">
              {t('affiliateTabs.profile.safetyNote')}
            </div>
            <PayoutFields f={{ ...f }} set={set} errors={errors} allowCommitment08={program.tax.allowCommitment08} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setEditPayout(false); setF(formFromPartner(partner)) }}>{t('affiliateTabs.profile.cancel')}</Button>
              <Button onClick={savePayout} disabled={updatePayout.isPending}>
                {updatePayout.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {t('affiliateTabs.profile.savePayout')}
              </Button>
            </div>
          </>
        )}
        <p className="text-xs text-muted-foreground">
          {t('affiliateTabs.profile.encryptNote')}
        </p>
      </Card>
    </div>
  )
}
